/** Strip optional ```json ... ``` wrapper from pasted text. */
export function stripJsonCodeFence(text) {
  let s = String(text || "").trim();
  if (!s) return s;
  const whole = /^```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```\s*$/i;
  const mWhole = s.match(whole);
  if (mWhole) return mWhole[1].trim();
  const inner = /```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```/i;
  const mInner = s.match(inner);
  if (mInner) return mInner[1].trim();
  return s;
}

export const JOB_OPENING_STATUSES = ["draft", "published", "closed", "archived"];
export const JOB_OPENING_EMPLOYMENT_TYPES = [
  "full_time",
  "part_time",
  "temporary",
  "internship",
  "freelance",
];
export const JOB_APPLICATION_STATUSES = [
  "new",
  "reviewing",
  "interview",
  "offer",
  "hired",
  "rejected",
  "withdrawn",
];
export const JOB_APPLICATION_SOURCES = ["manual", "website", "email", "other"];
export const JOB_OPENING_BULK_MAX = 50;

const OPENING_FIELDS = [
  "title",
  "slug",
  "short_description",
  "description",
  "responsibilities",
  "requirements",
  "requirement_items",
  "benefits",
  "location",
  "employment_type",
  "department_name",
  "salary_info",
  "work_percentage",
  "positions_count",
  "application_deadline",
  "starts_on",
  "contact_user_id",
  "status",
  "show_on_website",
  "sort_order",
];

const APPLICATION_FIELDS = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "city",
  "cover_letter",
  "linkedin_url",
  "status",
  "source",
  "notes",
  "rating",
  "available_from",
  "consent_privacy",
  "requirement_checks",
];

function parseError(code, message) {
  const err = new Error(message || code);
  err.code = code;
  return err;
}

function asString(value) {
  if (value == null) return "";
  return String(value).trim();
}

function normalizeApplication(raw) {
  if (!raw || typeof raw !== "object") return null;
  const first_name = asString(raw.first_name);
  const last_name = asString(raw.last_name);
  const email = asString(raw.email).toLowerCase();
  if (!first_name || !last_name || !email) return null;

  const status = asString(raw.status || "new").toLowerCase();
  if (raw.status && !JOB_APPLICATION_STATUSES.includes(status)) return null;
  const source = asString(raw.source || "manual").toLowerCase();
  if (raw.source && !JOB_APPLICATION_SOURCES.includes(source)) return null;

  const out = { first_name, last_name, email };
  APPLICATION_FIELDS.forEach((key) => {
    if (key === "first_name" || key === "last_name" || key === "email") return;
    if (raw[key] === undefined) return;
    if (key === "status") out.status = status;
    else if (key === "source") out.source = source;
    else if (key === "consent_privacy") out.consent_privacy = Boolean(raw.consent_privacy);
    else if (key === "rating" && raw.rating != null && raw.rating !== "") {
      const n = Number(raw.rating);
      if (Number.isFinite(n)) out.rating = n;
    } else {
      out[key] = raw[key];
    }
  });
  if (out.consent_privacy == null) out.consent_privacy = true;
  return out;
}

function normalizeOpening(raw) {
  if (!raw || typeof raw !== "object") return null;
  const title = asString(raw.title);
  if (!title) return null;

  const status = asString(raw.status || "draft").toLowerCase();
  if (raw.status && !JOB_OPENING_STATUSES.includes(status)) return null;
  const employment_type = asString(raw.employment_type || "full_time").toLowerCase();
  if (raw.employment_type && !JOB_OPENING_EMPLOYMENT_TYPES.includes(employment_type)) {
    return null;
  }

  const out = { title };
  OPENING_FIELDS.forEach((key) => {
    if (key === "title") return;
    if (raw[key] === undefined) return;
    if (key === "status") out.status = status;
    else if (key === "employment_type") out.employment_type = employment_type;
    else if (key === "show_on_website") out.show_on_website = Boolean(raw.show_on_website);
    else if (
      (key === "work_percentage" ||
        key === "positions_count" ||
        key === "sort_order" ||
        key === "contact_user_id") &&
      raw[key] != null &&
      raw[key] !== ""
    ) {
      const n = Number(raw[key]);
      if (Number.isFinite(n)) out[key] = n;
    } else {
      out[key] = raw[key];
    }
  });

  const apps = raw.applications || raw.candidates;
  if (Array.isArray(apps)) {
    out.applications = apps.map(normalizeApplication).filter(Boolean);
  }
  return out;
}

export function parseImportedJobOpeningsJson(text) {
  const trimmed = stripJsonCodeFence(text);
  if (!trimmed) throw parseError("empty");

  let data;
  try {
    data = JSON.parse(trimmed);
  } catch {
    throw parseError("invalid_json");
  }

  let atomic = false;
  let openings = [];

  if (Array.isArray(data)) {
    openings = data;
  } else if (data && typeof data === "object") {
    atomic = Boolean(data.atomic);
    if (Array.isArray(data.openings)) openings = data.openings;
    else if (Array.isArray(data.job_openings)) openings = data.job_openings;
    else if (data.opening && typeof data.opening === "object") openings = [data.opening];
    else if (data.title) openings = [data];
  } else {
    throw parseError("invalid_shape");
  }

  if (openings.length > JOB_OPENING_BULK_MAX) {
    throw parseError("too_many_openings", `Too many openings (max ${JOB_OPENING_BULK_MAX})`);
  }

  const dropReasons = [];
  const normalized = [];
  openings.forEach((row, idx) => {
    const item = normalizeOpening(row);
    if (item) normalized.push(item);
    else dropReasons.push(`openings[${idx}]`);
  });

  if (normalized.length === 0) throw parseError("no_valid_items");

  return {
    atomic,
    openings: normalized,
    droppedCount: dropReasons.length,
    dropReasons,
  };
}

export function serializeJobOpeningsPayloadForExport(payload) {
  return JSON.stringify(
    {
      atomic: Boolean(payload?.atomic),
      openings: Array.isArray(payload?.openings) ? payload.openings : [],
    },
    null,
    2
  );
}

export function serializeExistingOpeningsForAi(openings = []) {
  return (Array.isArray(openings) ? openings : []).map((row) => ({
    title: row.title,
    slug: row.slug,
    status: row.status,
    location: row.location,
    employment_type: row.employment_type,
  }));
}

export function formatExistingOpeningsOutline(openings = []) {
  const list = Array.isArray(openings) ? openings : [];
  if (!list.length) return "(ingen stillinger ennå)";
  return list
    .map(
      (row) =>
        `- ${row.title || "Uten tittel"} [${row.slug || "ingen-slug"}] ${row.status || "draft"}` +
        (row.location ? ` — ${row.location}` : "")
    )
    .join("\n");
}

export const SAMPLE_JOB_OPENINGS_JSON = serializeJobOpeningsPayloadForExport({
  atomic: false,
  openings: [
    {
      title: "Servicetekniker ventilasjon",
      slug: "servicetekniker-ventilasjon-eksempel",
      short_description: "Service og feilsøking på ventilasjonsanlegg.",
      description: "Vi søker servicetekniker til service og vedlikehold.",
      responsibilities: "- Serviceavtaler\n- Feilsøking\n- Dokumentasjon",
      requirement_items: [
        { id: "req_fagbrev", text: "Fagbrev eller tilsvarende" },
        { id: "req_license", text: "Førerkort klasse B" },
      ],
      requirements: "- Fagbrev eller tilsvarende\n- Førerkort klasse B",
      benefits: "- Servicebil\n- Kurs og utvikling",
      location: "Oslo",
      employment_type: "full_time",
      department_name: "Service",
      salary_info: "Etter avtale",
      work_percentage: 100,
      positions_count: 1,
      status: "draft",
      show_on_website: false,
      applications: [],
    },
  ],
});
